<template>
    <div class="bg-white shadow rounded-lg mb-6">
        <div class="relative z-0 rounded-t-lg flex">
            <div
                :class="[
                    'text-gray-400 bg-white border border-gray-200 rounded-t-lg relative flex-1 overflow-hidden py-4 px-4 text-center flex justify-center items-center',
                ]"
            >
                <ExclamationIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                Out of Stock
            </div>
        </div>

        <div
            class="px-4 py-5 sm:p-6 border border-t-0 border-gray-200 rounded-b-lg"
        >
            <div class="flex justify-between gap-4">
                <div class="md:hidden w-16 mx-auto shrink-0">
                    <slot name="product-image" />
                </div>

                <div class="md:mx-auto">
                    <p>
                        Unfortunately, we are temporarily
                        <span class="font-bold text-red-500">out of stock</span
                        >.
                    </p>

                    <p class="my-6">
                        We expect to be back in stock
                        {{ /\d/.test(inStockAt) ? "on" : "" }}
                        <span class="font-bold text-nutreance-green">{{
                            inStockAt
                        }}</span
                        >.
                    </p>
                </div>
            </div>

            <p class="text-center">
                To be notified first when this product is available again,
                please enter your email below:
            </p>

            <div class="px-4 sm:px-6 mt-4">
                <div class="mt-0 space-y-6">
                    <div>
                        <label
                            for="email"
                            class="block text-sm font-medium text-gray-700"
                        >
                            Email
                        </label>

                        <div class="mt-1 flex rounded-md shadow-sm">
                            <div
                                class="relative flex items-stretch grow focus-within:z-10"
                            >
                                <input
                                    @keydown.enter="subscribe"
                                    v-model="email"
                                    name="email"
                                    id="email"
                                    type="email"
                                    autocomplete="email"
                                    :class="[
                                        errors.email
                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                        'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                    ]"
                                    :aria-invalid="
                                        errors.email ? 'true' : 'false'
                                    "
                                    :aria-describedby="
                                        errors.email ? 'email-error' : ''
                                    "
                                />
                                <div
                                    v-if="errors.email"
                                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                >
                                    <ExclamationCircleIcon
                                        class="h-5 w-5 text-red-500"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                        </div>

                        <p
                            v-if="errors.email"
                            class="mt-2 text-sm text-red-600"
                            id="email-error"
                        >
                            {{ errors.email }}
                        </p>
                    </div>

                    <button
                        @click="subscribe"
                        name="subscribe"
                        type="button"
                        class="mt-4 md:mt-8 w-full py-3 shadow rounded-md text-base font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
                    >
                        <span class="flex items-center justify-center">
                            <BellIcon
                                v-if="!subscribed"
                                class="h-5 w-5 mr-2"
                                aria-hidden="true"
                            />
                            <CheckIcon
                                v-else
                                class="h-5 w-5 mr-2"
                                aria-hidden="true"
                            />
                            {{
                                !subscribed
                                    ? "Notify Me"
                                    : "You're On the List!"
                            }}
                        </span>
                    </button>

                    <p
                        v-if="subscribed"
                        class="text-center mt-2 text-sm text-nutreance-green"
                        id="email-success"
                    >
                        We will email you as soon as {{ product.name }} is back
                        in stock.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import {
    BellIcon,
    CheckIcon,
    ExclamationCircleIcon,
    ExclamationIcon,
} from "@heroicons/vue/solid";
import { DateTime } from "luxon";
import { subscribeToOutOfStockNotifications } from "../../api/mailCampaigns";

const props = defineProps<{
    product?: any;
}>();

const errors = reactive<Record<string, any>>({});
const email = ref("");
const subscribed = ref(false);
const inStockAt = computed(() => {
    return props.product.default_product_option.in_stock_at
        ? `${DateTime.fromISO(props.product.default_product_option.in_stock_at).toLocaleString(DateTime.DATE_FULL)}`
        : "in a few days";
});

function subscribe() {
    if (subscribed.value) {
        return;
    }

    subscribeToOutOfStockNotifications({
        product_id: props.product.id,
        email: email.value,
    })
        .then(() => {
            subscribed.value = true;
            clearErrors();
        })
        .catch((error: any) => {
            subscribed.value = false;

            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                errors[field] = error.response.data.errors[field][0];
            }
        });
}

function clearErrors() {
    for (const field in errors) {
        delete errors[field];
    }
}
</script>
